import { CalendarDays, Clock, CheckCircle2, User } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { KPICard } from "../../../components/admin/dashboard/KPICard";
import { AppointmentTable } from "../../../components/admin/dashboard/AppointmentTable";
import { Card } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Avatar, AvatarFallback } from "../../../components/ui/avatar";
import { Skeleton } from "../../../components/ui/skeleton";
import { useRole } from "../../../contexts/RoleContext";
import type { DashboardStats, Appointment, Doctor } from "../../../shared/schema";

interface DoctorDashboardProps {
  dateRange: "today" | "week";
}

export function DoctorDashboard({ dateRange }: DoctorDashboardProps) {
  const { currentDoctorId } = useRole();

  const { data: doctor } = useQuery<Doctor>({
    queryKey: ["/api/doctors", currentDoctorId],
    enabled: Boolean(currentDoctorId),
  });
  
  const { data: stats, isLoading: statsLoading } = useQuery<DashboardStats>({
    queryKey: ["/api/stats", { doctorId: currentDoctorId }],
    enabled: Boolean(currentDoctorId),
  });
  
  const { data: appointments, isLoading: appointmentsLoading } = useQuery<Appointment[]>({
    queryKey: ["/api/appointments", { doctorId: currentDoctorId, range: dateRange }],
    enabled: Boolean(currentDoctorId),
  });

  const { data: todayAppointments } = useQuery<Appointment[]>({
    queryKey: ["/api/appointments", { doctorId: currentDoctorId }],
    enabled: Boolean(currentDoctorId),
  });

  const upcomingAppointments = (() => {
    if (!todayAppointments) return [];
    const now = new Date();
    const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    return todayAppointments.filter(apt => apt.time >= currentTime).slice(0, 5);
  })();

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {statsLoading ? (
          <>
            {[1, 2, 3].map((i) => (
              <Card key={i} className="p-6 border border-card-border">
                <Skeleton className="h-4 w-24 mb-4" />
                <Skeleton className="h-8 w-16 mb-2" />
                <Skeleton className="h-3 w-32" />
              </Card>
            ))}
          </>
        ) : (
          <>
            <KPICard
              title="Bệnh nhân hôm nay"
              value={stats?.todayAppointments || 0}
              subtitle="Tổng số bệnh nhân trong ngày"
              icon={User}
              variant="primary"
            />
            <KPICard
              title="Lịch hẹn sắp tới"
              value={stats?.upcomingToday || 0}
              subtitle="Chờ khám trong hôm nay"
              icon={Clock}
              variant="warning"
            />
            <KPICard
              title="Hoàn thành tuần này"
              value={stats?.completedThisWeek || 0}
              subtitle="Ca khám đã hoàn thành"
              icon={CheckCircle2}
              variant="success"
            />
          </>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1 p-6 border border-card-border">
          <h3 className="text-base font-semibold text-foreground mb-4">
            Lịch hẹn sắp tới
          </h3>
          {upcomingAppointments.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <CalendarDays className="h-12 w-12 opacity-30 mb-2" />
              <p className="text-sm">Không có lịch hẹn sắp tới</p>
            </div>
          ) : (
            <div className="space-y-3">
              {upcomingAppointments.map((apt, index) => (
                <div 
                  key={apt.id}
                  className={`flex items-center gap-3 p-3 rounded-lg ${
                    index === 0 ? 'bg-primary/5 border border-primary/20' : 'bg-muted/50'
                  }`}
                  data-testid={`upcoming-appointment-${apt.id}`}
                >
                  <div className="flex flex-col items-center justify-center min-w-[50px]">
                    <span className="text-lg font-bold text-foreground">{apt.time}</span>
                    {index === 0 && (
                      <Badge variant="default" className="mt-1">
                        Tiếp theo
                      </Badge>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="text-xs">
                          {apt.patientName.split(' ').map(n => n[0]).join('').slice(0, 2)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {apt.patientName}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {apt.specialtyName} - {apt.room}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <div className="lg:col-span-2">
          {appointmentsLoading ? (
            <Card className="p-6 border border-card-border">
              <Skeleton className="h-4 w-48 mb-6" />
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            </Card>
          ) : (
            <AppointmentTable
              appointments={appointments || []}
              showDepartment={false}
              showDoctor={false}
              title={dateRange === "today" ? "Lịch khám hôm nay" : "Lịch khám tuần này"}
              emptyMessage="Không có lịch khám nào"
            />
          )}
        </div>
      </div>
    </div>
  );
}
